import type { HiddenCoinView } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { LoadingRegion } from "@screener/uikit/components/loading-skeleton";
import { PanelBlock } from "@screener/uikit/components/panel";
import { Skeleton } from "@screener/uikit/components/skeleton";
import { useState } from "react";
import type { HiddenCoinsState } from "./hidden-match";
import type { HiddenCoinOutcome } from "./use-hidden-coins";

/**
 * Раздел «Скрытые монеты» страницы «Настройки» (§75 п. 3, §127 п. 7): список скрытых монет пользователя и
 * действие «Вернуть» у каждой. Единственный путь вернуть монету (§58
 * «Отвергнуто» п. 6). Часть экрана, не UIKit: знает про протокол и про домен.
 * Состояние списка и запросы держит каркас (`useHiddenCoins`), здесь только
 * разметка.
 */

export const HIDDEN_COINS_MENU_LABEL = "Скрытые монеты";
export const HIDDEN_COINS_LOADING = "Читаем список скрытых монет…";
export const HIDDEN_COINS_EMPTY = "Скрытых монет нет.";
export const HIDDEN_COINS_LOAD_FAILED =
  "Список скрытых монет прочитать не удалось — это не значит, что он пуст.";
export const HIDDEN_COINS_RETRY = "Повторить";
export const HIDDEN_COINS_UNHIDE = "Вернуть";

/** Подпись действия для читающих с экрана: название монеты в самой кнопке. */
export function unhideButtonLabel(base: string): string {
  return `Вернуть монету ${base}`;
}

/**
 * Неудача возврата названа словами, и монета остаётся в списке. `400` не
 * советует повторить: демон отверг саму запись.
 */
export function unhideFailureText(outcome: Exclude<HiddenCoinOutcome, "ok">, base: string): string {
  return outcome === "invalid"
    ? `Демон отверг возврат монеты ${base}; она осталась в списке.`
    : `Монета ${base} не возвращена и осталась в списке — попробуйте ещё раз.`;
}

/** Список по алфавиту; исходный массив не меняется. */
export function sortedHiddenCoins(coins: readonly HiddenCoinView[]): HiddenCoinView[] {
  return [...coins].sort((left, right) =>
    left.base < right.base ? -1 : left.base > right.base ? 1 : 0,
  );
}

export interface HiddenCoinsPanelProps {
  state: HiddenCoinsState;
  /** Монеты, чьё собственное действие ещё в пути — «Вернуть» у них недоступна. */
  pendingBases: ReadonlySet<string>;
  onUnhide: (base: string) => Promise<HiddenCoinOutcome>;
  /** Перечитать список — «Повторить», когда чтение не удалось. */
  onReload: () => void;
}

export function HiddenCoinsPanel({
  state,
  pendingBases,
  onUnhide,
  onReload,
}: HiddenCoinsPanelProps) {
  const [failure, setFailure] = useState<string | null>(null);

  if (state.status === "loading") {
    return (
      <PanelBlock>
        <LoadingRegion label={HIDDEN_COINS_LOADING} className="flex flex-col gap-2">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-2/3" />
        </LoadingRegion>
      </PanelBlock>
    );
  }
  if (state.status === "unavailable") {
    return (
      <PanelBlock className="items-start gap-2">
        <p role="alert" className="text-sm text-destructive">
          {HIDDEN_COINS_LOAD_FAILED}
        </p>
        <Button size="sm" variant="outline" onClick={onReload}>
          {HIDDEN_COINS_RETRY}
        </Button>
      </PanelBlock>
    );
  }
  if (state.coins.length === 0) {
    return (
      <PanelBlock>
        <p className="text-sm text-muted-foreground">{HIDDEN_COINS_EMPTY}</p>
      </PanelBlock>
    );
  }

  async function unhide(base: string): Promise<void> {
    setFailure(null);
    const outcome = await onUnhide(base);
    if (outcome !== "ok") {
      setFailure(unhideFailureText(outcome, base));
    }
  }

  return (
    <div className="flex flex-col">
      <ul className="flex flex-col">
        {sortedHiddenCoins(state.coins).map((coin) => (
          <li
            key={coin.base}
            className="flex min-h-12 items-center justify-between gap-3 border-t border-border px-5 py-2"
          >
            <span className="text-[13px] font-medium text-foreground">{coin.base}</span>
            <Button
              size="sm"
              variant="outline"
              aria-label={unhideButtonLabel(coin.base)}
              disabled={pendingBases.has(coin.base)}
              onClick={() => void unhide(coin.base)}
            >
              {HIDDEN_COINS_UNHIDE}
            </Button>
          </li>
        ))}
      </ul>
      {failure !== null ? (
        <p role="alert" className="border-t border-border px-5 py-3 text-xs text-destructive">
          {failure}
        </p>
      ) : null}
    </div>
  );
}
